// CampaignForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {goalLabel} from './GoalOptions';
import SegmentStep from './steps/SegmentStep';
import CampaignSettings from './steps/CampaignSettings';
import MessageContent from './steps/MessageContent';
import EmailCampaignSettings from './steps/EmailCampaignSettings';
import EmailContentStep from './steps/EmailContentStep';

const Section = ({ index, title, hint, valid, children }) => (
  <section className='border border-gray-200 rounded-lg p-5 mb-6'>
    <div className='flex items-center mb-4'>
      <span
        className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold mr-3 ${
          valid ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {valid ? '✓' : index}
      </span>
      <div>
        <h3 className='text-lg font-semibold text-gray-800'>{title}</h3>
        {hint && <p className='text-xs text-gray-500'>{hint}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Row = ({ label, children, muted }) => (
  <div className='flex gap-4 py-2 border-b border-gray-100 last:border-0'>
    <div className='w-40 shrink-0 text-xs uppercase tracking-wide text-gray-500 pt-0.5'>{label}</div>
    <div className={`flex-1 text-sm ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{children}</div>
  </div>
);

const segmentTemplateName = (formData) => formData.smsTemplateName;

const formatSendTime = (formData) => {
  if (formData.sendTime !== 'later') return 'Send now';
  if (!formData.scheduledTime) return 'Not set';

  const d = new Date(formData.scheduledTime);
  return Number.isNaN(d.getTime())
    ? formData.scheduledTime
    : `Scheduled for ${d.toLocaleString()}`;
};


// Проверка перед запуском email-кампании: все параметры из п.7 ТЗ.
const EmailReviewRows = ({ formData, segments }) => (
  <>
    <Row label='Name'>{formData.name}</Row>
    <Row label='Channel'>Email</Row>
    <Row label='Email template'>{formData.emailTemplateName || '—'}</Row>
    <Row label='Subject'>
      {formData.emailSubject || '—'}
      {formData.emailPreheader && <span className='block text-xs text-gray-500'>{formData.emailPreheader}</span>}
    </Row>
    <Row label='Segments'>
      {segments.length ? segments.map((segment) => segment.label).join(', ') : '—'}
    </Row>
    <Row label='Recipients'>
      {formData.emailRecipients ?? '—'} emails
      <span className='block text-xs text-gray-400'>
        Unique players and email addresses, minus unsubscribed / bounced. The exact audience is fixed at launch.
      </span>
    </Row>
    <Row label='From'>
      {formData.emailSenderName ? `${formData.emailSenderName} <${formData.emailSenderLabel}>` : '—'}
    </Row>
    <Row label='Goal'>
      {goalLabel(formData.goalType)}
    </Row>
    <Row label='Attribution window'>{formData.attributionWindowHours}h</Row>
    <Row label='Send time' muted={formData.sendTime === 'later' && !formData.scheduledTime}>
      {formatSendTime(formData)}
    </Row>
  </>
);

const ReviewDialog = ({ formData, segment, segments = [], isEdit, isSubmitting, error, onConfirm, onCancel }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const geos = (segment && segment.geos) || [];
  const uncovered = (segment && segment.uncovered_geos) || [];
  const confirmLabel = isEdit
    ? 'Save changes'
    : formData.sendTime === 'later'
    ? 'Schedule campaign'
    : 'Send now';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-gray-900/50' />

      <div className='relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-bold text-gray-900'>Review campaign</h3>
          <p className='text-xs text-gray-500 mt-0.5'>
            Check the details below — sending cannot be undone.
          </p>
        </div>

        <div className='px-6 py-3 overflow-y-auto'>
          {formData.channel === 'email' ? (
            <EmailReviewRows formData={formData} segments={segments} />
          ) : (
          <>
          <Row label='Name'>{formData.name}</Row>

          <Row label='Segment'>
            {segment ? (
              <div className='space-y-1'>
                <div>
                  {segment.label}{' '}
                  <span className='text-xs text-gray-400'>({segment.segment_type})</span>
                </div>
                <div className='flex flex-wrap gap-1'>
                  {geos.length ? (
                    geos.map((geo) => (
                      <span key={geo} className='text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700'>
                        {geo}
                      </span>
                    ))
                  ) : (
                    <span className='text-xs text-gray-400'>no GEO</span>
                  )}
                </div>
              </div>
            ) : (
              '—'
            )}
          </Row>

          <Row label='Recipients'>
            {segment ? `${segment.players_count} players` : '—'}
            <span className='block text-xs text-gray-400'>
              The exact audience is snapshotted when the campaign starts.
            </span>
          </Row>

          <Row label='Sender ID'>
            {formData.senderIdId ? 'Pinned binding' : 'Auto — by player GEO and priority'}
            {uncovered.length > 0 && (
              <span className='block text-xs text-amber-700 mt-1'>
                No Sender ID binding for {uncovered.join(', ')} — those players will fail.
              </span>
            )}
          </Row>

          <Row label='Template'>
            {segmentTemplateName(formData) || 'Manual text (no template)'}
          </Row>

          <Row label='SMS text'>
            <div className='bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap break-words'>
              {formData.messageText}
            </div>
            {formData.fullShortUrl && (
              <span className='block text-xs text-gray-500 mt-1'>
                Short link: {formData.fullShortUrl}
              </span>
            )}
          </Row>

          <Row label='Goal'>
            {goalLabel(formData.goalType)} · attribution window{' '}
            {formData.attributionWindowHours}h
          </Row>

          {formData.cautiousSendingEnabled && (
            <Row label='Cautious sending'>
              Test batch {formData.batchPercent}% of the audience, wait{' '}
              {formData.waitTimeMinutes} min
              {formData.ctrThreshold ? `, stop if CTR < ${formData.ctrThreshold}%` : ''}
              {formData.cpcThreshold ? `, stop if CPC > $${formData.cpcThreshold}` : ''}
            </Row>
          )}

          <Row label='Send time' muted={formData.sendTime === 'later' && !formData.scheduledTime}>
            {formatSendTime(formData)}
          </Row>
          </>
          )}
        </div>

        {error && (
          <div className='px-6 pt-3'>
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        )}

        <div className='px-6 py-4 border-t border-gray-200 flex justify-end gap-3'>
          <button
            className='px-4 py-2 rounded border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50'
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Back to editing
          </button>
          <button
            className={`px-4 py-2 rounded bg-green-500 hover:bg-green-700 text-white font-bold ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const CampaignForm = ({ segments = [], initialData, campaignId, mode }) => {
  const isEdit = mode === 'edit';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validity, setValidity] = useState({
    audience: false,
    settings: false,
    message: false,
  });

  const validSetters = useMemo(() => {
    const make = (key) => (value) =>
      setValidity((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    return { audience: make('audience'), settings: make('settings'), message: make('message') };
  }, []);

  const [showReview, setShowReview] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState(() => {
    const defaults = {
      channel: 'sms',
      emailTemplateId: null,
      emailTemplateName: null,
      emailSenderId: null,
      segmentId: null,
      segmentIds: [],
      segmentGeo: null,
      segmentGeos: [],
      segmentUncoveredGeos: [],
      senderIdId: null,

      country: null,
      lastDepositStart: '',
      lastDepositEnd: '',
      depositsCount: '',
      ban: false,
      manualIds: '',
      usePhones: false,
      audienceCount: 0,

      name: '',
      sender: null,
      sendTime: 'now',
      scheduledTime: '',

      messageText: '',
      messageLink: '',
      smsTemplateId: null,
      smsTemplateName: null,

      cautiousSendingEnabled: false,
      batchPercent: 10,
      waitTimeMinutes: 30,
      ctrThreshold: '',
      cpcThreshold: '',

      goalType: 'deposit',
      attributionWindowHours: 6,
    };

    if (!initialData) return defaults;

    const cleaned = Object.fromEntries(
      Object.entries(initialData).filter(([, v]) => v !== null && v !== undefined)
    );
    const merged = { ...defaults, ...cleaned };
    merged.segmentIds = initialData.segmentIds || (initialData.segmentId ? [initialData.segmentId] : []);
    merged.scheduledTime = isoToLocalInput(initialData.scheduledTime);
    return merged;
  });

  const updateFormData = (newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const selectedSegment = segments.find(
    (s) => String(s.value) === String(formData.segmentId)
  );

  const isEmail = formData.channel === 'email';
  const selectedSegments = segments.filter((s) => (formData.segmentIds || []).map(String).includes(String(s.value)));

  const missing = (isEmail
    ? [
        !formData.segmentIds?.length && 'segments',
        formData.segmentIds?.length > 0 && !validity.audience && 'segments with email recipients',
        !formData.name?.trim() && 'campaign name',
        !formData.emailSenderId && 'email sender',
        !formData.emailTemplateId && 'email template',
        formData.sendTime === 'later' && !formData.scheduledTime && 'send time',
      ]
    : [
        !formData.segmentId && 'segment',
        !formData.name?.trim() && 'campaign name',
        !formData.messageText?.trim() && 'SMS text',
        formData.sendTime === 'later' && !formData.scheduledTime && 'send time',
        !validity.settings && 'a Sender ID route for the segment GEO',
      ]
  ).filter(Boolean);

  // Смена канала: шаги другие, их валидность пересчитается заново.
  const switchChannel = (channel) => {
    if (channel === formData.channel) return;
    setValidity({ audience: false, settings: false, message: false });
    updateFormData({ channel, goalType: 'deposit', cautiousSendingEnabled: false,
      segmentIds: channel === 'email' && formData.segmentId ? [formData.segmentId] : [], emailRecipients: null });
  };

  const isFormValid =
    validity.audience && validity.settings && validity.message && missing.length === 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Конвертируем локальное время пользователя в UTC для сервера
      // Браузер отправляет время без таймзоны (например "2025-11-06T14:30")
      // Нам нужно парсить год, месяц, день, час, минуты и создать Date объект,
      // который потом toISOString() автоматически приведет к UTC
      const submitData = { ...formData };
      
      if (submitData.sendTime === 'later' && submitData.scheduledTime) {
        const [datePart, timePart] = submitData.scheduledTime.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        
        const localDate = new Date(year, month - 1, day, hour, minute);
        
        submitData.scheduledTime = localDate.toISOString();
      }

      const response = await fetch(
        isEdit ? `/campaigns/${campaignId}` : '/campaigns',
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          },
          body: JSON.stringify({ campaign: submitData }),
        }
      );

      if (response.ok) {
        window.location.href = '/campaigns';
        return;
      }

      let errorData = null;
      try {
        errorData = await response.json();
      } catch (e) {
      }
      const message =
        (errorData && errorData.errors && errorData.errors.join('. ')) ||
        `Could not save the campaign (HTTP ${response.status})`;

      setSubmitError(message);
      setIsSubmitting(false);
    } catch (error) {
      setSubmitError(error.message || 'Could not save the campaign');
      setIsSubmitting(false);
    }
  };

  return (
    <div className='max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-xl relative'>
      <h2 className='text-2xl font-bold mb-6'>
        {isEdit ? 'Edit Campaign' : 'Create Campaign'}
      </h2>

      {isSubmitting && (
        <div className='absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50'>
          <div className='text-center'>
            <div className='w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4'></div>
            <div className='text-xl font-semibold text-gray-800 animate-pulse'>
              {isEdit ? 'Saving your campaign...' : 'Creating your campaign...'}
            </div>
            <div className='text-sm text-gray-600 mt-2'>
              Please wait while we process your request
            </div>
          </div>
        </div>
      )}

      <div className='mb-6'>
        <p className='text-gray-700 text-sm font-bold mb-2'>Channel</p>
        <div className='grid grid-cols-2 gap-3'>
          {[
            ['sms', 'SMS', 'Short text message with a tracked link'],
            ['email', 'Email', 'Email template from Messages, sent via an email sender'],
          ].map(([value, label, hint]) => (
            <button
              type='button'
              key={value}
              disabled={isEdit && formData.channel !== value}
              onClick={() => switchChannel(value)}
              className={`text-left border rounded-lg px-4 py-3 transition-colors ${
                formData.channel === value
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                  : 'border-gray-200 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
            >
              <div className='font-semibold text-gray-900'>{label}</div>
              <div className='text-xs text-gray-500'>{hint}</div>
            </button>
          ))}
        </div>
      </div>

      <Section index={1} title='Audience' valid={validity.audience}>
        <SegmentStep
          key={formData.channel}
          formData={formData}
          updateFormData={updateFormData}
          segments={segments}
          setIsFormValid={validSetters.audience}
          channel={formData.channel}
        />
      </Section>

      <Section index={2} title='Campaign settings' valid={validity.settings}>
        {isEmail ? (
          <EmailCampaignSettings
            formData={formData}
            updateFormData={updateFormData}
            setIsFormValid={validSetters.settings}
          />
        ) : (
          <CampaignSettings
            formData={formData}
            updateFormData={updateFormData}
            setIsFormValid={validSetters.settings}
          />
        )}
      </Section>

      <Section index={3} title={isEmail ? 'Email' : 'Message'} valid={validity.message}>
        {isEmail ? (
          <EmailContentStep
            formData={formData}
            updateFormData={updateFormData}
            setIsFormValid={validSetters.message}
          />
        ) : (
          <MessageContent
            formData={formData}
            updateFormData={updateFormData}
            setIsFormValid={validSetters.message}
          />
        )}
      </Section>

      {!isFormValid && missing.length > 0 && (
        <p className='text-right text-xs text-gray-500 mb-2'>
          Fill in {missing.join(', ')} to continue.
        </p>
      )}

      <div className='flex justify-end'>
        <button
            className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition-all duration-300 ${
              !isFormValid || isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : ''
            } ${isSubmitting ? 'scale-95' : ''}`}
            onClick={() => setShowReview(true)}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? (
              <span className='flex items-center'>
                <svg
                  className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
                {isEdit ? 'Saving...' : 'Creating...'}
              </span>
            ) : isEdit ? (
              'Review changes'
            ) : (
              'Review campaign'
            )}
        </button>
      </div>

      {showReview && (
        <ReviewDialog
          segments={selectedSegments}
          formData={formData}
          segment={selectedSegment}
          isEdit={isEdit}
          isSubmitting={isSubmitting}
          error={submitError}
          onConfirm={handleSubmit}
          onCancel={() => setShowReview(false)}
        />
      )}
    </div>
  );
};

export default CampaignForm;
